import { EventEmitter } from 'events';
import * as http from 'http';
import * as https from 'https';
import * as zlib from 'zlib';
import WebSocket from 'ws';
import type { TikTokLiveOptions, TikTokLiveEvents, RoomInfo, LiveEvent, StreamInfo } from './types.js';
import {
    decodeProto,
    getStr,
    getBytes,
    buildHeartbeat,
    buildImEnterRoom,
    buildAck,
    parseWebcastResponse,
} from './proto.js';

const DEFAULT_UA = 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/131.0.0.0 Safari/537.36';
const DEFAULT_SIGN_SERVER = 'https://api.tik.tools';

function httpGet(url: string, headers: Record<string, string>, agent?: any): Promise<{
    status: number;
    headers: http.IncomingHttpHeaders;
    body: Buffer;
}> {
    return new Promise((resolve, reject) => {
        const mod = url.startsWith('https') ? https : http;
        const opts: any = { headers };
        if (agent) opts.agent = agent;
        const req = mod.get(url, opts, (res) => {
            const chunks: Buffer[] = [];
            const enc = res.headers['content-encoding'];
            const stream = (enc === 'gzip' || enc === 'br')
                ? res.pipe(enc === 'br' ? zlib.createBrotliDecompress() : zlib.createGunzip())
                : res;
            stream.on('data', (c: Buffer) => chunks.push(c));
            stream.on('end', () => resolve({
                status: res.statusCode || 0,
                headers: res.headers,
                body: Buffer.concat(chunks),
            }));
            stream.on('error', reject);
        });
        req.on('error', reject);
        req.setTimeout(15_000, () => { req.destroy(); reject(new Error('Request timeout')); });
    });
}

function getWsHost(clusterRegion: string): string {
    if (!clusterRegion) return 'webcast-ws.tiktok.com';
    const r = clusterRegion.toLowerCase();
    if (r.startsWith('eu') || r.includes('eu')) return 'webcast-ws.eu.tiktok.com';
    if (r.startsWith('us') || r.includes('us')) return 'webcast-ws.us.tiktok.com';
    return 'webcast-ws.tiktok.com';
}

export class TikTokLive extends EventEmitter {
    private ws: WebSocket | null = null;
    private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
    private reconnectAttempts = 0;
    private intentionalClose = false;
    private _connected = false;
    private _destroyed = false;
    private _eventCount = 0;
    private _roomId = '';
    // Cache host identities from battle events for enriching battleArmies
    // Bounded to MAX_BATTLE_HOSTS entries to prevent unbounded memory growth
    private static readonly MAX_BATTLE_HOSTS = 50;
    private _battleHosts = new Map<string, { id: string; nickname: string; uniqueId: string; profilePicture?: string }>();
    // Track in-flight host resolution requests to avoid duplicate API calls
    private _pendingHostResolves = new Set<string>();

    private readonly uniqueId: string;
    private readonly signServerUrl: string;
    private readonly apiKey: string;
    private readonly autoReconnect: boolean;
    private readonly maxReconnectAttempts: number;
    private readonly heartbeatInterval: number;
    private readonly debug: boolean;
    private readonly _presetRoomId: string;
    private readonly _presetSessionId: string;
    private readonly proxyUrl: string;
    private readonly mode: 'direct' | 'relayed';

    constructor(options: TikTokLiveOptions) {
        super();
        this.setMaxListeners(20); // Prevent memory leak warnings
        this.uniqueId = options.uniqueId.replace(/^@/, '');
        this.signServerUrl = (options.signServerUrl || DEFAULT_SIGN_SERVER).replace(/\/$/, '');
        if (!options.apiKey) throw new Error('apiKey is required. Get a free key at https://tik.tools');
        this.apiKey = options.apiKey;
        this.autoReconnect = options.autoReconnect ?? true;
        this.maxReconnectAttempts = options.maxReconnectAttempts ?? 5;
        this.heartbeatInterval = options.heartbeatInterval ?? 10_000;
        this.debug = options.debug ?? false;
        this._presetRoomId = options.roomId || '';
        this._presetSessionId = options.sessionId || '';
        this.proxyUrl = options.proxy || '';
        this.mode = options.mode || 'direct';
    }

    /**
     * Build an HttpsProxyAgent when `proxy` option is set, else undefined.
     * Lazy-required so users without proxy don't pay the dependency cost.
     */
    private getProxyAgent(): any {
        if (!this.proxyUrl) return undefined;
        try {
            // Lazy import — keeps https-proxy-agent optional for non-proxy users.
            const { HttpsProxyAgent } = require('https-proxy-agent');
            return new HttpsProxyAgent(this.proxyUrl);
        } catch (e: any) {
            throw new Error(
                `proxy option requires 'https-proxy-agent' package. Install it: npm i https-proxy-agent`
            );
        }
    }

    async connect(): Promise<void> {
        if (this._destroyed) throw new Error('Client has been destroyed. Create a new instance.');
        this.intentionalClose = false;

        // Relayed mode: skip TikTok credential dance, connect to TikTools' managed
        // pool. We open the TikTok session from our rotated proxy fleet and forward
        // decoded events as JSON. SDK consumers see the same `client.on(...)` API.
        if (this.mode === 'relayed') {
            return this._connectRelayed();
        }

        let ttwid = this._presetSessionId;
        let roomId = this._presetRoomId;
        let clusterRegion = '';
        let preSignedUrl = '';
        let preSignedCookies = '';
        let preSignedUA = '';

        if (roomId && ttwid) {
            // Both pre-resolved â€” skip TikTok page fetch entirely
            console.log(`[TikTokLive] Using preset roomId=${roomId} + sessionId (skip TikTok fetch)`);
        } else if (roomId && !ttwid) {
            // Have roomId but need ttwid â€” get it from sign server (proxied)
            console.log(`[TikTokLive] Have roomId=${roomId}, fetching ttwid from sign server...`);
            try {
                const credResp = await fetch(`${this.signServerUrl}/webcast/ws_credentials`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'x-api-key': this.apiKey },
                    body: JSON.stringify({ unique_id: this.uniqueId, room_id: roomId }),
                });
                console.log(`[TikTokLive] ws_credentials response: ${credResp.status}`);
                if (credResp.ok) {
                    const credData = await credResp.json() as any;
                    if (credData.status_code === 0 && credData.data?.signed_url) {
                        // Use pre-signed URL + cookies directly â€” skip ttwid flow
                        preSignedUrl = credData.data.signed_url;
                        preSignedCookies = credData.data.cookies || '';
                        preSignedUA = credData.data.user_agent || DEFAULT_UA;
                        ttwid = '__presigned__'; // Sentinel to pass the ttwid check
                        console.log(`[TikTokLive] âœ… Got pre-signed URL from sign server`);
                    } else {
                        console.log(`[TikTokLive] âš ï¸ ws_credentials bad response: ${JSON.stringify(credData).substring(0, 120)}`);
                    }
                }
            } catch (e: any) { console.log(`[TikTokLive] âŒ ws_credentials failed: ${e.message}`); }
            // Fallback: try direct TikTok fetch
            if (!ttwid) {
                console.log(`[TikTokLive] Fallback: direct TikTok fetch for ttwid...`);
                try {
                    const resp = await httpGet(`https://www.tiktok.com/@${this.uniqueId}/live`, {
                        'User-Agent': DEFAULT_UA,
                        'Accept': 'text/html',
                    }, this.getProxyAgent());
                    for (const sc of [resp.headers['set-cookie'] || []].flat()) {
                        if (typeof sc === 'string' && sc.startsWith('ttwid=')) {
                            ttwid = sc.split(';')[0].split('=').slice(1).join('=');
                            break;
                        }
                    }
                    console.log(`[TikTokLive] Direct fetch ttwid: ${ttwid ? 'âœ…' : 'âŒ none'}`);
                } catch (e: any) { console.log(`[TikTokLive] âŒ Direct TikTok fetch failed: ${e.message}`); }
            }
        } else {
            // No presets â€” try sign server first (works from VPS), then fall back to direct fetch
            console.log(`[TikTokLive] No presets for @${this.uniqueId}. Resolving via sign server (${this.signServerUrl})...`);
            // Step 1: Resolve roomId via sign server's /webcast/room_id (uses proxies)
            try {
                const roomResp = await fetch(`${this.signServerUrl}/webcast/room_id`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'x-api-key': this.apiKey },
                    body: JSON.stringify({ unique_id: this.uniqueId }),
                });
                console.log(`[TikTokLive] room_id response: ${roomResp.status}`);
                if (roomResp.ok) {
                    const roomData = await roomResp.json() as any;
                    if (roomData.status_code === 0 && roomData.data?.room_id) {
                        roomId = roomData.data.room_id;
                        console.log(`[TikTokLive] âœ… Got roomId from sign server: ${roomId}`);
                    } else {
                        console.log(`[TikTokLive] âš ï¸ room_id response: ${JSON.stringify(roomData).substring(0, 120)}`);
                    }
                }
            } catch (e: any) { console.log(`[TikTokLive] âŒ room_id request failed: ${e.message}`); }

            // Step 2: Get ttwid via sign server's /webcast/ws_credentials
            if (roomId) {
                try {
                    const credResp = await fetch(`${this.signServerUrl}/webcast/ws_credentials`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', 'x-api-key': this.apiKey },
                        body: JSON.stringify({ unique_id: this.uniqueId, room_id: roomId }),
                    });
                    console.log(`[TikTokLive] ws_credentials response: ${credResp.status}`);
                    if (credResp.ok) {
                        const credData = await credResp.json() as any;
                        if (credData.data?.cookies) {
                            const m = credData.data.cookies.match(/ttwid=([^;]+)/);
                            if (m) { ttwid = m[1]; console.log(`[TikTokLive] âœ… Got ttwid from sign server`); }
                            else console.log(`[TikTokLive] âš ï¸ No ttwid in cookies: ${credData.data.cookies.substring(0, 60)}`);
                        } else {
                            console.log(`[TikTokLive] âš ï¸ No cookies in ws_credentials: ${JSON.stringify(credData).substring(0, 120)}`);
                        }
                    }
                } catch (e: any) { console.log(`[TikTokLive] âŒ ws_credentials failed: ${e.message}`); }
            }

            // Step 3: Fallback â€” direct TikTok page fetch (works on dev, blocked on most VPS)
            if (!roomId || !ttwid) {
                console.log(`[TikTokLive] Fallback: direct TikTok fetch (roomId=${roomId || 'none'}, ttwid=${ttwid ? 'yes' : 'none'})...`);
                try {
                    const resp = await httpGet(`https://www.tiktok.com/@${this.uniqueId}/live`, {
                        'User-Agent': DEFAULT_UA,
                        'Accept': 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8',
                        'Accept-Encoding': 'gzip, deflate, br',
                        'Accept-Language': 'en-US,en;q=0.9',
                    }, this.getProxyAgent());

                    if (!ttwid) {
                        for (const sc of [resp.headers['set-cookie'] || []].flat()) {
                            if (typeof sc === 'string' && sc.startsWith('ttwid=')) {
                                ttwid = sc.split(';')[0].split('=').slice(1).join('=');
                                break;
                            }
                        }
                    }

                    if (!roomId) {
                        const html = resp.body.toString();
                        const sigiMatch = html.match(/id="SIGI_STATE"[^>]*>([^<]+)/);
                        if (sigiMatch) {
                            try {
                                const json = JSON.parse(sigiMatch[1]);
                                const jsonStr = JSON.stringify(json);
                                const m = jsonStr.match(/"roomId"\s*:\s*"(\d+)"/);
                                if (m) roomId = m[1];
                            } catch { }
                        }

                        const crMatch = html.match(/"clusterRegion"\s*:\s*"([^"]+)"/);
                        clusterRegion = crMatch ? crMatch[1] : '';
                    }
                    console.log(`[TikTokLive] Direct fetch result: roomId=${roomId || 'none'}, ttwid=${ttwid ? 'yes' : 'none'}`);
                } catch (e: any) { console.log(`[TikTokLive] âŒ Direct TikTok fetch failed: ${e.message}`); }
            }
        }

        if (!ttwid) throw new Error('Failed to obtain session cookie');
        if (!roomId) throw new Error(`User @${this.uniqueId} is not currently live`);
        this._roomId = roomId;
        const wsHost = getWsHost(clusterRegion);

        const wsParams = new URLSearchParams({
            version_code: '270000', device_platform: 'web', cookie_enabled: 'true',
            screen_width: '1920', screen_height: '1080', browser_language: 'en-US',
            browser_platform: 'Win32', browser_name: 'Mozilla',
            browser_version: DEFAULT_UA.split('Mozilla/')[1] || '5.0',
            browser_online: 'true', tz_name: Intl.DateTimeFormat().resolvedOptions().timeZone,
            app_name: 'tiktok_web', sup_ws_ds_opt: '1', update_version_code: '2.0.0',
            compress: 'gzip', webcast_language: 'en', ws_direct: '1', aid: '1988',
            live_id: '12', app_language: 'en', client_enter: '1', room_id: roomId,
            identity: 'audience', history_comment_count: '6', last_rtt: '0',
            heartbeat_duration: '10000', resp_content_type: 'protobuf', did_rule: '3',
        });

        const rawWsUrl = `https://${wsHost}/webcast/im/ws_proxy/ws_reuse_supplement/?${wsParams}`;

        const signUrl = `${this.signServerUrl}/webcast/sign_url`;

        let wsUrl: string;
        try {
            const signResp = await fetch(signUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-api-key': this.apiKey,
                },
                body: JSON.stringify({ url: rawWsUrl }),
            });
            const signData = await signResp.json() as Record<string, any>;

            if (signData.status_code === 0 && signData.data?.signed_url) {
                wsUrl = (signData.data.signed_url as string).replace(/^https:\/\//, 'wss://');
            } else {
                wsUrl = rawWsUrl.replace(/^https:\/\//, 'wss://');
            }
        } catch {
            wsUrl = rawWsUrl.replace(/^https:\/\//, 'wss://');
        }

        return new Promise<void>((resolve, reject) => {
            const finalWsUrl = preSignedUrl
                ? preSignedUrl.replace(/^https:\/\//, 'wss://')
                : wsUrl;
            const finalHeaders = preSignedUrl
                ? { 'User-Agent': preSignedUA, 'Cookie': preSignedCookies, 'Origin': 'https://www.tiktok.com' }
                : { 'User-Agent': DEFAULT_UA, 'Cookie': `ttwid=${ttwid}`, 'Origin': 'https://www.tiktok.com' };
            console.log(`[TikTokLive] Connecting WS: ${preSignedUrl ? 'pre-signed URL' : 'self-built URL'}`);
            const proxyAgent = this.getProxyAgent();
            this.ws = new WebSocket(finalWsUrl, {
                headers: finalHeaders,
                ...(proxyAgent ? { agent: proxyAgent } : {}),
            });

            this.ws.on('open', () => {
                this._connected = true;
                this.reconnectAttempts = 0;

                this.ws!.send(buildHeartbeat(roomId));
                this.ws!.send(buildImEnterRoom(roomId));
                this.startHeartbeat(roomId);

                const roomInfo: RoomInfo = {
                    roomId,
                    wsHost,
                    clusterRegion,
                    connectedAt: new Date().toISOString(),
                };

                this.emit('connected');
                this.emit('roomInfo', roomInfo);
                resolve();
            });

            this.ws.on('message', (rawData: Buffer) => {
                this.handleFrame(Buffer.from(rawData));
            });

            this.ws.on('close', (code, reason) => {
                const wasConnected = this._connected;
                this._connected = false;
                this.stopHeartbeat();

                const reasonStr = reason?.toString() || '';
                this.emit('disconnected', code, reasonStr);

                // Only auto-reconnect if we WERE connected (connection drop, not initial 403 failure)
                if (wasConnected && !this.intentionalClose && this.autoReconnect && this.reconnectAttempts < this.maxReconnectAttempts) {
                    this.reconnectAttempts++;
                    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts - 1), 30_000);
                    setTimeout(() => this.connect().catch(e => this.emit('error', e)), delay);
                }
            });

            this.ws.on('error', (err) => {
                // Only emit 'error' for connection drops (was connected).
                // Initial 403 failures are handled via promise rejection.
                if (this._connected) {
                    this.emit('error', err);
                }
                if (!this._connected) reject(err);
            });
        });
    }

    disconnect(): void {
        this.intentionalClose = true;
        this.stopHeartbeat();
        if (this.ws) {
            this.ws.removeAllListeners();
            this.ws.close(1000);
            this.ws = null;
        }
        this._connected = false;
    }

    /**
     * Fully destroy the client, releasing all resources and listeners.
     * After calling destroy(), the instance cannot be reused â€” create a new one.
     */
    destroy(): void {
        this.disconnect();
        this._destroyed = true;
        this._battleHosts.clear();
        this._eventCount = 0;
        this._roomId = '';
        this.removeAllListeners();
    }

    get connected(): boolean {
        return this._connected;
    }

    get destroyed(): boolean {
        return this._destroyed;
    }

    get eventCount(): number {
        return this._eventCount;
    }

    get roomId(): string {
        return this._roomId;
    }

    /**
     * Get live stream video URLs for a TikTok user.
     * Returns FLV & HLS URLs segmented by quality (FULL_HD1, HD1, SD1, SD2).
     * This is a standalone method â€” no WebSocket connection required.
     * 
     * @example
     * ```ts
     * const stream = await TikTokLive.getStreamUrl({
     *   uniqueId: 'username',
     *   apiKey: 'your-api-key',
     * });
     * if (stream.alive) {
     *   console.log(stream.streamUrls.HD1?.hls); // HLS URL for HD quality
     *   console.log(stream.flvPullUrl);           // Best FLV URL
     * }
     * ```
     */
    static async getStreamUrl(options: {
        uniqueId: string;
        apiKey: string;
        signServerUrl?: string;
        quality?: string;
    }): Promise<StreamInfo> {
        const serverUrl = (options.signServerUrl || DEFAULT_SIGN_SERVER).replace(/\/$/, '');

        const resp = await fetch(`${serverUrl}/webcast/room_video`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'x-api-key': options.apiKey,
            },
            body: JSON.stringify({ unique_id: options.uniqueId.replace(/^@/, '') }),
        });

        const data = await resp.json() as Record<string, any>;

        if (data.status_code !== 0) {
            throw new Error(data.error || 'Failed to get stream URL');
        }

        const d = data.data;
        return {
            roomId: d.room_id,
            alive: d.alive ?? false,
            streamUrls: d.stream_urls || {},
            defaultQuality: d.default_quality || '',
            flvPullUrl: d.flv_pull_url || undefined,
            hlsPullUrl: d.hls_pull_url || undefined,
        };
    }

    /**
     * Resolve a TikTok username to the streamer's full public profile —
     * numeric user ID, secUid, nickname, bio, avatars, follower stats.
     *
     * Useful when you need the numeric TikTok ID for a username (the inverse
     * of `resolve_user_ids`, which only goes userId → username).
     *
     * Requires Pro tier+. Cached server-side for 24h.
     *
     * @example
     * ```ts
     * const profile = await TikTokLive.getUserProfile({
     *   uniqueId: 'dalga.ahmedov',
     *   apiKey: 'YOUR_KEY',
     * });
     * console.log(profile.id);                    // "7355610677036581896" (numeric)
     * console.log(profile.nickname);              // display name
     * console.log(profile.stats.followerCount);   // 12345
     * console.log(profile.avatarLarger);          // CDN URL
     * ```
     */
    static async getUserProfile(options: {
        uniqueId: string;
        apiKey: string;
        signServerUrl?: string;
        /** Set `true` to bypass the 24h server cache (e.g. after the user updated their bio). */
        nocache?: boolean;
    }): Promise<{
        id: string;
        uniqueId: string;
        secUid: string;
        nickname: string;
        signature: string;
        verified: boolean;
        avatarThumb: string;
        avatarMedium: string;
        avatarLarger: string;
        stats: { followerCount: number; followingCount: number; heartCount: number; videoCount: number };
    }> {
        const serverUrl = (options.signServerUrl || DEFAULT_SIGN_SERVER).replace(/\/$/, '');
        const uname = options.uniqueId.replace(/^@/, '');
        const qs = new URLSearchParams({ unique_id: uname });
        if (options.nocache) qs.set('nocache', '1');

        const resp = await fetch(`${serverUrl}/webcast/user_profile?${qs}`, {
            headers: { 'x-api-key': options.apiKey },
        });

        const data = await resp.json() as Record<string, any>;
        if (data.status_code !== 0 || !data.data?.profile) {
            throw new Error(data.error || `Failed to get user profile for @${uname}`);
        }
        return data.data.profile;
    }

    /**
     * Instance shortcut — fetch the profile of the streamer this client is
     * connected to (or any other user if `uniqueId` is passed).
     */
    async getUserProfile(uniqueId?: string) {
        return TikTokLive.getUserProfile({
            uniqueId: uniqueId || this.uniqueId,
            apiKey: this.apiKey,
            signServerUrl: this.signServerUrl,
        });
    }

    on<K extends keyof TikTokLiveEvents>(event: K, listener: TikTokLiveEvents[K]): this {
        return super.on(event, listener as (...args: any[]) => void);
    }

    once<K extends keyof TikTokLiveEvents>(event: K, listener: TikTokLiveEvents[K]): this {
        return super.once(event, listener as (...args: any[]) => void);
    }

    off<K extends keyof TikTokLiveEvents>(event: K, listener: TikTokLiveEvents[K]): this {
        return super.off(event, listener as (...args: any[]) => void);
    }

    emit<K extends keyof TikTokLiveEvents>(event: K, ...args: Parameters<TikTokLiveEvents[K]>): boolean {
        return super.emit(event, ...args);
    }

    private handleFrame(buf: Buffer): void {
        try {
            const fields = decodeProto(buf);
            const idField = fields.find(f => f.fn === 2 && f.wt === 0);
            const id = idField ? idField.value as bigint : 0n;
            const type = getStr(fields, 7);
            const binary = getBytes(fields, 8);

            if (id > 0n && this.ws?.readyState === WebSocket.OPEN) {
                this.ws.send(buildAck(id));
            }

            if (type === 'msg' && binary && binary.length > 0) {
                let inner = binary;
                if (inner.length > 2 && inner[0] === 0x1f && inner[1] === 0x8b) {
                    try { inner = zlib.gunzipSync(inner); } catch { }
                }

                const events = parseWebcastResponse(inner);
                for (const evt of events) {
                    this._eventCount++;

                    // Cache host identities from battle events (hosts are in users[])
                    if (evt.type === 'battle') {
                        for (const team of evt.teams) {
                            const host = team.users.find(u => u.user.id === team.hostUserId);
                            if (host) {
                                this._battleHosts.set(team.hostUserId, host.user);
                                team.hostUser = host.user;
                            }
                        }
                        // Evict oldest entries if cache exceeds limit
                        if (this._battleHosts.size > TikTokLive.MAX_BATTLE_HOSTS) {
                            const excess = this._battleHosts.size - TikTokLive.MAX_BATTLE_HOSTS;
                            const keys = [...this._battleHosts.keys()].slice(0, excess);
                            for (const key of keys) this._battleHosts.delete(key);
                        }
                    }

                    // Enrich battleArmies teams with cached host identities
                    if (evt.type === 'battleArmies') {
                        const unresolvedIds: string[] = [];
                        for (const team of evt.teams) {
                            const host = team.users.find(u => u.user.id === team.hostUserId);
                            if (host) {
                                team.hostUser = host.user;
                                this._battleHosts.set(team.hostUserId, host.user);
                            } else {
                                const cached = this._battleHosts.get(team.hostUserId);
                                if (cached) {
                                    team.hostUser = cached;
                                } else if (team.hostUserId && team.hostUserId !== '0') {
                                    unresolvedIds.push(team.hostUserId);
                                }
                            }
                        }
                        // Defer emission until hosts are resolved
                        if (unresolvedIds.length > 0) {
                            this.resolveHostUsers(unresolvedIds, evt).catch(() => { });
                            continue; // Don't emit yet â€” resolveHostUsers will emit after resolution
                        }
                    }

                    this.emit('event', evt);
                    this.emit(evt.type as keyof TikTokLiveEvents, evt as any);
                }
            }
        } catch { }
    }

    /**
     * Resolve unknown host user IDs via the sign server API.
     * Caches results and re-emits the battleArmies event with enriched hostUser data.
     */
    private async resolveHostUsers(userIds: string[], originalEvt: LiveEvent): Promise<void> {
        // Filter out IDs already being resolved
        const toResolve = userIds.filter(id => !this._pendingHostResolves.has(id));
        if (toResolve.length === 0) {
            // All IDs being resolved â€” still emit the event as-is
            this.emit('event', originalEvt);
            this.emit(originalEvt.type as keyof TikTokLiveEvents, originalEvt as any);
            return;
        }
        for (const id of toResolve) this._pendingHostResolves.add(id);

        try {
            const url = `${this.signServerUrl}/webcast/resolve_user_ids`;
            const res = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-api-key': this.apiKey,
                },
                body: JSON.stringify({ user_ids: toResolve }),
                signal: AbortSignal.timeout(5000),
            });

            if (res.ok) {
                const json = await res.json() as { data?: Record<string, { userId: string; username: string | null; nickname?: string | null; avatarUrl?: string | null }> };
                if (json.data) {
                    for (const [uid, info] of Object.entries(json.data)) {
                        if (info.username) {
                            const hostUser = {
                                id: uid,
                                uniqueId: info.username,
                                nickname: info.nickname || info.username,
                                profilePicture: info.avatarUrl || undefined,
                            };
                            this._battleHosts.set(uid, hostUser);
                        }
                    }

                    // Enrich the original event with resolved hosts
                    if (originalEvt.type === 'battleArmies') {
                        for (const team of originalEvt.teams) {
                            if (!team.hostUser) {
                                const cached = this._battleHosts.get(team.hostUserId);
                                if (cached) team.hostUser = cached;
                            }
                        }
                    }
                }
            }
        } catch {
            // Resolution failed â€” event will still be emitted below (unenriched)
        } finally {
            for (const id of toResolve) this._pendingHostResolves.delete(id);
            // Always emit the event â€” enriched or not
            this.emit('event', originalEvt);
            this.emit(originalEvt.type as keyof TikTokLiveEvents, originalEvt as any);
        }
    }

    private startHeartbeat(roomId: string): void {
        this.stopHeartbeat();
        this.heartbeatTimer = setInterval(() => {
            if (this.ws?.readyState === WebSocket.OPEN) {
                this.ws.send(buildHeartbeat(roomId));
            }
        }, this.heartbeatInterval);
    }

    private stopHeartbeat(): void {
        if (this.heartbeatTimer) {
            clearInterval(this.heartbeatTimer);
            this.heartbeatTimer = null;
        }
    }

    /**
     * Relayed-mode connect. Opens a plain WebSocket to TikTools' managed relay
     * endpoint. Events arrive pre-decoded as `{event, data}` JSON envelopes.
     * The SDK re-emits them on the same event names as Direct mode so user
     * code is identical regardless of mode.
     */
    private async _connectRelayed(): Promise<void> {
        const host = this.signServerUrl
            .replace(/^https:\/\//, 'wss://')
            .replace(/^http:\/\//, 'ws://');
        const wsUrl = `${host}/?uniqueId=${encodeURIComponent(this.uniqueId)}&apiKey=${encodeURIComponent(this.apiKey)}`;
        if (this.debug) console.log(`[TikTokLive] Relayed mode: ${wsUrl.replace(this.apiKey, '***')}`);

        return new Promise<void>((resolve, reject) => {
            this.ws = new WebSocket(wsUrl);

            let firstOpen = true;
            this.ws.on('open', () => {
                this._connected = true;
                this.reconnectAttempts = 0;
                if (firstOpen) { firstOpen = false; resolve(); }
                this.emit('connected');
            });

            this.ws.on('message', (raw: Buffer) => {
                try {
                    const msg = JSON.parse(raw.toString());
                    // Relay sometimes sends plain-text ping/pong heartbeats.
                    if (typeof msg !== 'object' || !msg) return;
                    const evName = msg.event;
                    const evData = msg.data ?? msg;
                    if (!evName) return;
                    if (evName === 'connected' || evName === '_journal' || evName === 'ping' || evName === 'pong') return;
                    if (evName === 'roomInfo') {
                        const ri: RoomInfo = {
                            roomId: evData.roomId || '',
                            wsHost: evData.wsHost || '',
                            clusterRegion: evData.clusterRegion || '',
                            connectedAt: evData.connectedAt || new Date().toISOString(),
                        };
                        this.emit('roomInfo', ri);
                        return;
                    }
                    // Re-emit decoded event under its native name.
                    (this as any).emit(evName, evData);
                    (this as any).emit('event', evData);
                } catch { }
            });

            this.ws.on('close', (code: number, reason: Buffer) => {
                this._connected = false;
                const reasonStr = reason?.toString() || '';
                this.emit('disconnected', code, reasonStr);
                if (!this.intentionalClose && this.autoReconnect && this.reconnectAttempts < this.maxReconnectAttempts) {
                    this.reconnectAttempts++;
                    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30_000);
                    if (this.debug) console.log(`[TikTokLive] Relayed reconnect in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
                    setTimeout(() => { this._connectRelayed().catch(() => {}); }, delay);
                }
            });

            this.ws.on('error', (err: Error) => {
                if (firstOpen) { firstOpen = false; reject(err); }
                else this.emit('error', err);
            });
        });
    }
}
